import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Company,
  CompanyIntroduction,
  CREATE_COMPANY_INTRODUCTION_INPUT,
  DELETE_COMPANY_INTRODUCTION_INPUT,
  ErrorResponse,
  GET_COMPANY,
  GET_COMPANY_INTRODUCTION,
  UploadedFile,
} from '@frontend/data-access';
import {
  CompanyAvatar,
  FileUpload,
  supportedVideoFormats,
} from '@frontend/shared/ui';
import { App, Button, Form, Modal } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useState } from 'react';

import ClaimCompanyProfile from './claim';
import EditInformation from './edit-actions/information';

const ProfileCover: React.FC<{ data: Company }> = ({ data }) => {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [form] = useForm();
  const [openImageUploadModal, setOpenImageUploadModal] = useState(false);
  const [editProfileModal, setEditProfileModal] = useState(false);
  const videos = Form.useWatch('videos', form);
  const [introductionVideo, setIntroductionVideo] = useState<UploadedFile>();
  const [isOpenClaim, setIsOpenClaim] = useState<boolean>(false);

  const companyType = useMemo(
    () => [
      {
        label: t('company_type_accelerator'),
        value: 'ACCELERATOR',
      },
      {
        label: t('company_type_consultancy'),
        value: 'CONSULTANCY',
      },
      {
        label: t('company_type_corporation'),
        value: 'CORPORATE',
      },
      {
        label: t('company_type_government'),
        value: 'GOVERNMENT',
      },
      {
        label: t('company_type_startup'),
        value: 'STARTUP',
      },
      {
        label: t('company_type_research'),
        value: 'RESEARCH',
      },
      {
        label: t('company_type_investment'),
        value: 'INVESTMENT',
      },
    ],
    [t]
  );

  const [getCompany, { data: dataCompany }] = useLazyQuery<{
    company: Company;
  }>(GET_COMPANY);
  const [getCompanyIntro, { data: dataIntro, refetch }] = useLazyQuery<{
    listCompanyIntroduction: CompanyIntroduction[];
  }>(GET_COMPANY_INTRODUCTION);

  useEffect(() => {
    if (dataIntro?.listCompanyIntroduction?.length) {
      setIntroductionVideo(dataIntro.listCompanyIntroduction[0].fileData);
    } else {
      setIntroductionVideo(undefined);
    }
  }, [dataIntro]);

  const handleIntroCompleted = () => {
    setOpenImageUploadModal(false);
    refetch();
    setIntroductionVideo(undefined);
  };

  useEffect(() => {
    if (data?.id) {
      getCompany({ variables: { id: data.id } });
      getCompanyIntro({ variables: { id: data.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCompany, data?.id]);

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [updateCompanyIntroductionVideo, { loading: introLoading }] =
    useMutation(CREATE_COMPANY_INTRODUCTION_INPUT, {
      onCompleted: handleIntroCompleted,
      onError: handleError,
    });

  const [deleteCompanyIntroduction, { loading: deleteLoading }] = useMutation(
    DELETE_COMPANY_INTRODUCTION_INPUT,
    {
      onCompleted: handleIntroCompleted,
      onError: handleError,
    }
  );

  const handleUploadIntro = (file: UploadedFile) => {
    updateCompanyIntroductionVideo({
      variables: {
        input: {
          companyId: data?.id,
          fileData: file,
        },
      },
    });
  };

  const handleDeleteIntro = () => {
    deleteCompanyIntroduction({
      variables: {
        input: {
          companyId: data?.id,
          companyIntroductionId: dataIntro?.listCompanyIntroduction[0]?.id,
        },
      },
    });
  };

  const handleUploadModal = () => {
    setOpenImageUploadModal(!openImageUploadModal);
  };

  const getFileByIndex = (index: number) => {
    try {
      if (videos[index]) {
        return [
          {
            uid: videos[index]?.id,
            name: videos[index]?.metadata?.filename,
            url: videos[index]?.url,
          },
        ];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const getCompanyType = () => {
    return companyType.find(
      (item) => item.value === dataCompany?.company?.companyType
    )?.label;
  };

  const handleEditModal = () => {
    setEditProfileModal(!editProfileModal);
  };

  const RenderStatus = () => {
    return dataCompany?.company?.approvalStatus === 'denied' ? (
      <div className="space-x-3">
        <span className="py-1 px-3 bg-red-200 rounded-full text-red-800 text-sm">
          {t('hidden')}
        </span>
        <span>
          (
          {t('reason_result', {
            reason: dataCompany?.company?.applications?.[0]?.rejectDesc || '-',
          })}
          )
        </span>
      </div>
    ) : null;
  };

  const getIntroBanner = (file: UploadedFile) => {
    switch (file.state) {
      case 'CLEAN':
        return (
          <div className="flex flex-col items-end">
            <div className="w-full rounded-3xl sm:h-48 md:h-80">
              <video
                className="rounded-3xl object-cover h-full w-full"
                src={introductionVideo?.url}
                controls
              />
            </div>
            {dataCompany?.company?.isOwner && (
              <Button
                danger
                type="link"
                onClick={() => handleDeleteIntro()}
                loading={deleteLoading}
              >
                {t('remove')}
              </Button>
            )}
          </div>
        );
      case 'INFECTED':
        if (dataCompany?.company?.isOwner)
          return (
            <div className="flex w-full rounded-3xl bg-gray-200 border-dashed border-gray-500 border overflow-hidden items-center transition-all duration-200 p-5">
              <div className="flex flex-row gap-2 items-center w-full">
                <div className="grow bg-gray-200 rounded-full flex flex-row justify-between items-center p-2 flex-wrap md:flex-nowrap">
                  <div className="flex gap-3 items-center">
                    <PlayCircleOutlined />
                    <div>
                      <div>
                        {file.metadata?.filename?.slice(0, 15) + '...' ||
                          t('no_file_name')}
                      </div>
                      <div>({t('virus_infected')})</div>
                    </div>
                  </div>
                </div>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  shape="circle"
                  onClick={() => handleDeleteIntro()}
                />
              </div>
            </div>
          );
        else return;
      case 'PROCESSING':
        if (dataCompany?.company?.isOwner)
          return (
            <div className="flex w-full rounded-3xl bg-gray-200 border-dashed border-gray-500 border overflow-hidden items-center transition-all duration-200 p-5">
              <div className="flex flex-row gap-2 items-center w-full">
                <div className="grow bg-gray-200 rounded-full flex flex-row justify-between items-center p-2 flex-wrap md:flex-nowrap">
                  <div className="flex gap-3 items-center">
                    <PlayCircleOutlined />
                    <div>
                      <div>{t('processing')}:</div>
                      <div>
                        {file.metadata?.filename?.slice(0, 15) + '...' ||
                          t('no_file_name')}
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  shape="circle"
                  onClick={() => handleDeleteIntro()}
                />
              </div>
            </div>
          );
        else return;
      case 'NOT_IN_MIME':
        if (dataCompany?.company?.isOwner)
          return (
            <div className="flex w-full rounded-3xl bg-gray-200 border-dashed border-gray-500 border overflow-hidden items-center transition-all duration-200 p-5">
              <div className="flex flex-row gap-2 items-center w-full">
                <div className="grow bg-gray-200 rounded-full flex flex-row justify-between items-center p-2 flex-wrap md:flex-nowrap">
                  <div className="flex gap-3 items-center">
                    <PlayCircleOutlined />
                    <div>
                      <div>{t('file_type_not_supported')}:</div>
                      <div>
                        {file.metadata?.filename?.slice(0, 15) + '...' ||
                          t('no_file_name')}
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  shape="circle"
                  onClick={() => handleDeleteIntro()}
                />
              </div>
            </div>
          );
        else return;
      default:
        return;
    }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row flex-wrap items-center text-center justify-center md:text-start md:justify-start gap-4 md:gap-9">
        {dataCompany?.company && (
          <CompanyAvatar
            size={120}
            company={dataCompany.company}
            shape="circle"
            showBadge={dataCompany.company.companyPortfolio?.isScvPortfolio}
            badgeSize={30}
            isClickable={false}
          />
        )}
        <div className="md:flex-auto space-y-2">
          <div className="flex gap-4 items-center">
            <div className="text-4xl font-medium md:ml-0">
              {dataCompany?.company?.name}
            </div>
            {dataCompany?.company?.isOwner && (
              <Button
                className="mt-1"
                data-test="profile-edit-btn"
                shape="circle"
                onClick={handleEditModal}
              >
                <EditOutlined />
              </Button>
            )}
          </div>
          <RenderStatus />
          <div className="py-1 px-3 bg-gray-200 rounded-full text-gray-800 text-sm inline-block whitespace-nowrap">
            {getCompanyType()}
          </div>
          <div>
            {dataCompany?.company?.isMember &&
              !dataCompany?.company?.isOwner && (
                <>
                  <Button
                    type="primary"
                    shape="round"
                    onClick={() => setIsOpenClaim(true)}
                  >
                    {t('claim_profile')}
                  </Button>
                  <ClaimCompanyProfile
                    companyId={dataCompany?.company.id}
                    isOpenClaim={isOpenClaim}
                    setIsOpenClaim={setIsOpenClaim}
                  />
                </>
              )}
          </div>
        </div>
        <div className="md:w-80 w-full flex-shrink-0">
          {introductionVideo
            ? getIntroBanner(introductionVideo)
            : dataCompany?.company?.isOwner && (
                <div
                  className="flex flex-col w-full rounded-3xl bg-gray-200 border-dashed border-gray-500 hover:border-primary-400 border h-80 overflow-hidden justify-center items-center cursor-pointer transition-all duration-200"
                  onClick={handleUploadModal}
                >
                  <div className="text-white bg-gray-500 rounded-full w-10 h-10 flex items-center justify-center mb-2">
                    <UploadOutlined />
                  </div>
                  <p>{t('upload_intro_video')}</p>
                  <Button
                    shape="round"
                    type="primary"
                    data-test="upload-intro-video-btn"
                  >
                    {t('upload_video')}
                  </Button>
                </div>
              )}
        </div>
      </div>
      <Modal
        title={t('upload_video')}
        open={openImageUploadModal}
        onOk={() => handleUploadModal()}
        confirmLoading={introLoading}
        onCancel={() => handleUploadModal()}
        okButtonProps={{
          shape: 'round',
        }}
        cancelButtonProps={{
          shape: 'round',
        }}
      >
        <Form.Item noStyle data-test="intro-video-upload-input">
          <FileUpload
            id="intro-video-upload-input"
            description={
              <div className="mb-4">{t('upload_video_description')}</div>
            }
            maxFileSize={524288000}
            supportedFileTypes={supportedVideoFormats}
            className="w-full overflow-auto"
            onUploadSuccess={(file) => handleUploadIntro(file)}
            onRemove={undefined}
            defaultFiles={getFileByIndex(0)}
          />
        </Form.Item>
      </Modal>
      <Modal
        title={t('edit')}
        open={editProfileModal}
        onCancel={handleEditModal}
        footer={false}
      >
        <EditInformation
          companyId={data?.id}
          onFinish={handleEditModal}
          onCancel={handleEditModal}
        />
      </Modal>
    </>
  );
};

export default ProfileCover;
